import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FileText, Shield, DollarSign, Library, ChevronRight, TrendingUp,
  AlertCircle, Upload, Plus, Download, FileDown, Clock, Target,
  Activity, Zap, BarChart3, Layers, ArrowUpRight, Eye
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import ExportToolbar from "../components/ExportToolbar";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Cell, PieChart, Pie, Tooltip as RechartsTooltip, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Animated Counter ─── */
const AnimatedNumber = ({ value, duration = 1200, prefix = "", suffix = "" }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof value !== "number" || isNaN(value)) { setDisplay(value); return; }
    let start = 0;
    const step = value / (duration / 16);
    const id = setInterval(() => {
      start += step;
      if (start >= value) { setDisplay(value); clearInterval(id); }
      else setDisplay(Math.round(start));
    }, 16);
    return () => clearInterval(id);
  }, [value, duration]);
  return <span ref={ref}>{prefix}{typeof display === "number" ? display.toLocaleString() : display}{suffix}</span>;
};

/* ─── Risk Gauge SVG ─── */
const RiskGauge = ({ score }) => {
  const pct = Math.min(score, 100);
  const color = pct > 70 ? "#FF4F00" : pct > 40 ? "#F59E0B" : "#10B981";
  const dash = (pct / 100) * 188;
  return (
    <div className="relative w-20 h-20">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r="30" fill="none" stroke="#E4E4E7" strokeWidth="6" />
        <circle cx="40" cy="40" r="30" fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={`${dash} 188`} strokeLinecap="square"
          style={{ transition: "stroke-dasharray 1.2s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-black font-['Chivo']" style={{ color }}>{score}</span>
      </div>
    </div>
  );
};

/* ─── Sparkline Mini Chart ─── */
const Sparkline = ({ data, color = "#0047AB", height = 32 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !data?.length) return;
    const ctx = c.getContext("2d");
    const w = (c.width = c.offsetWidth * 2);
    const h = (c.height = height * 2);
    ctx.scale(2, 2);
    const cw = w / 2, ch = h / 2;
    const max = Math.max(...data), min = Math.min(...data);
    const range = max - min || 1;
    ctx.clearRect(0, 0, cw, ch);
    // gradient fill
    const grad = ctx.createLinearGradient(0, 0, 0, ch);
    grad.addColorStop(0, color + "30");
    grad.addColorStop(1, color + "05");
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * cw;
      const y = ch - ((v - min) / range) * (ch - 4) - 2;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.lineTo(cw, ch);
    ctx.lineTo(0, ch);
    ctx.fillStyle = grad;
    ctx.fill();
    // line
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * cw;
      const y = ch - ((v - min) / range) * (ch - 4) - 2;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, [data, color, height]);
  return <canvas ref={ref} style={{ width: "100%", height }} />;
};

/* ─── Custom Recharts Tooltip ─── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 text-white px-3 py-2 text-xs font-medium border border-zinc-700">
      <p className="font-bold uppercase tracking-wide mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

/* ─── Format Currency ─── */
const fmtVal = (v) => {
  if (!v && v !== 0) return "$0";
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const RISK_COLORS = ["#10B981", "#F59E0B", "#FF4F00", "#DC2626"];
const FRAMEWORK_COLORS = ["#0047AB", "#FF4F00", "#10B981", "#8B5CF6", "#EC4899", "#F59E0B", "#64748B"];

/* ═══════════════════════════════════════════════
   DASHBOARD VIEW
   ═══════════════════════════════════════════════ */
const DashboardView = ({ setActiveView, contracts, clauses }) => {
  const [animReady, setAnimReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnimReady(true), 100); return () => clearTimeout(t); }, []);

  /* ─── Computed Stats ─── */
  const stats = useMemo(() => {
    const totalValue = contracts.reduce((s, c) => s + (c.contract_value || 0), 0);
    const avgRisk = contracts.length > 0
      ? Math.round(contracts.reduce((s, c) => s + (c.risk_score || 0), 0) / contracts.length)
      : 0;
    const highRisk = contracts.filter(c => (c.risk_score || 0) > 70).length;
    const draftCount = contracts.filter(c => (c.status || "draft") === "draft").length;
    const approvedCount = contracts.filter(c => c.status === "approved").length;
    return { totalValue, avgRisk, highRisk, draftCount, approvedCount };
  }, [contracts]);

  /* ─── Risk Distribution Data ─── */
  const riskDistribution = useMemo(() => {
    const bins = [
      { name: "Low (0-30)", range: [0, 30], count: 0, fill: "#10B981" },
      { name: "Medium (31-55)", range: [31, 55], count: 0, fill: "#F59E0B" },
      { name: "High (56-75)", range: [56, 75], count: 0, fill: "#FF4F00" },
      { name: "Critical (76+)", range: [76, 100], count: 0, fill: "#DC2626" },
    ];
    contracts.forEach(c => {
      const r = c.risk_score || 0;
      const bin = bins.find(b => r >= b.range[0] && r <= b.range[1]);
      if (bin) bin.count++;
    });
    return bins;
  }, [contracts]);

  /* ─── Framework Distribution ─── */
  const frameworkData = useMemo(() => {
    const map = {};
    contracts.forEach(c => {
      const fw = c.framework || "Unknown";
      map[fw] = (map[fw] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [contracts]);

  /* ─── Sparkline mock data based on contracts ─── */
  const sparkData = useMemo(() => {
    const base = contracts.length || 1;
    return Array.from({ length: 12 }, (_, i) =>
      Math.max(1, Math.round(base * (0.3 + 0.7 * Math.sin((i / 11) * Math.PI)) + (Math.random() - 0.5) * base * 0.2))
    );
  }, [contracts]);

  const riskSparkData = useMemo(() => {
    const base = stats.avgRisk || 30;
    return Array.from({ length: 12 }, (_, i) =>
      Math.max(5, Math.round(base + Math.sin(i * 0.8) * 15 + (Math.random() - 0.5) * 10))
    );
  }, [stats.avgRisk]);

  const delay = (i) => ({ animationDelay: `${i * 80}ms` });

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="dashboard-view" data-export-region>

        {/* Export bar */}
        <div className="flex justify-end" data-no-print>
          <ExportToolbar title="Dashboard Overview" pdfFilename="dashboard" />
        </div>

        {/* ── HERO BANNER ── */}
        <div
          className={`relative overflow-hidden border-2 border-black bg-zinc-950 text-white p-8 md:p-10 brutal-shadow-lg transition-all duration-700 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={delay(0)}
          data-testid="dashboard-hero"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 40px, #fff 40px, #fff 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, #fff 40px, #fff 41px)"
            }}
          />
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF4F00] opacity-10 blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0047AB] opacity-10 blur-[80px]" />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 bg-[#10B981] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">System Active</span>
              <Badge className="bg-[#FF4F00] text-white border-none text-[10px] uppercase tracking-widest px-2 py-0">
                v3.0
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase font-['Chivo'] mb-3 leading-[1.1]">
              Command<br />
              <span className="text-[#FF4F00]">Center</span>
            </h1>
            <p className="text-sm text-zinc-400 max-w-lg mb-6 leading-relaxed">
              AI-powered contract intelligence across 15+ international frameworks.
              Monitor risks, track value, and manage your entire project portfolio.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setActiveView("generator")}
                className="btn-brutal bg-[#FF4F00] hover:bg-[#e64500] text-white px-5 py-2.5 h-auto text-xs"
                data-testid="cta-generate"
              >
                <Plus size={14} className="mr-2" />
                New Contract
              </Button>
              <Button
                onClick={() => setActiveView("delay-analysis")}
                variant="outline"
                className="btn-brutal border border-zinc-600 text-zinc-300 hover:text-white hover:border-white px-5 py-2.5 h-auto text-xs bg-transparent"
                data-testid="cta-delay-analysis"
              >
                <Clock size={14} className="mr-2" />
                Delay Analysis
              </Button>
              <Button
                onClick={() => setActiveView("analytics")}
                variant="outline"
                className="btn-brutal border border-zinc-600 text-zinc-300 hover:text-white hover:border-white px-5 py-2.5 h-auto text-xs bg-transparent"
                data-testid="cta-analytics"
              >
                <BarChart3 size={14} className="mr-2" />
                Analytics
              </Button>
            </div>
          </div>
        </div>

        {/* ── KPI CARDS ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: "Active Contracts",
              value: contracts.length,
              icon: FileText,
              color: "#0047AB",
              spark: sparkData,
              sub: `${stats.draftCount} draft`,
            },
            {
              label: "Average Risk",
              value: stats.avgRisk,
              icon: Shield,
              color: stats.avgRisk > 60 ? "#FF4F00" : stats.avgRisk > 40 ? "#F59E0B" : "#10B981",
              spark: riskSparkData,
              sub: `${stats.highRisk} high risk`,
            },
            {
              label: "Portfolio Value",
              value: stats.totalValue,
              icon: DollarSign,
              color: "#0047AB",
              isCurrency: true,
              sub: `${contracts.length} contracts`,
            },
            {
              label: "Clause Library",
              value: clauses.length,
              icon: Library,
              color: "#FF4F00",
              sub: "7 frameworks",
            },
          ].map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <div
                key={kpi.label}
                className={`group relative border-2 border-zinc-200 bg-white p-5 transition-all duration-300 hover:border-black hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 cursor-default ${
                  animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${(i + 1) * 100}ms` }}
                data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 border border-zinc-200 group-hover:border-black transition-colors">
                    <Icon size={16} style={{ color: kpi.color }} />
                  </div>
                  {kpi.spark && (
                    <div className="w-16 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Sparkline data={kpi.spark} color={kpi.color} height={24} />
                    </div>
                  )}
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">{kpi.label}</p>
                <p className="text-2xl lg:text-3xl font-black font-['Chivo'] leading-none">
                  {kpi.isCurrency ? (
                    fmtVal(kpi.value)
                  ) : (
                    <AnimatedNumber value={kpi.value} />
                  )}
                </p>
                <p className="text-[11px] text-zinc-400 mt-1.5">{kpi.sub}</p>
                {/* Accent corner */}
                <div
                  className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-l-[20px] border-l-transparent transition-all group-hover:border-t-[24px] group-hover:border-l-[24px]"
                  style={{ borderTopColor: kpi.color }}
                />
              </div>
            );
          })}
        </div>

        {/* ── CHARTS ROW ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Risk Distribution - 3 cols */}
          <div
            className={`lg:col-span-3 border-2 border-zinc-200 bg-white p-6 transition-all duration-500 ${
              animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "500ms" }}
            data-testid="risk-distribution-chart"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide">Risk Distribution</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">{contracts.length} contracts analyzed</p>
              </div>
              <div className="flex items-center gap-2">
                <RiskGauge score={stats.avgRisk} />
              </div>
            </div>
            {contracts.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={riskDistribution} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "Chivo", fill: "#71717a" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "Chivo", fill: "#71717a" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Contracts" radius={0}>
                    {riskDistribution.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center border border-dashed border-zinc-200">
                <p className="text-xs text-zinc-400 uppercase tracking-wide">Generate contracts to see risk distribution</p>
              </div>
            )}
          </div>

          {/* Framework Breakdown - 2 cols */}
          <div
            className={`lg:col-span-2 border-2 border-zinc-200 bg-white p-6 transition-all duration-500 ${
              animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "600ms" }}
            data-testid="framework-chart"
          >
            <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide mb-1">Frameworks</h2>
            <p className="text-[11px] text-zinc-400 mb-4">Contract distribution by framework</p>
            {frameworkData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={frameworkData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={65}
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {frameworkData.map((_, idx) => (
                        <Cell key={idx} fill={FRAMEWORK_COLORS[idx % FRAMEWORK_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {frameworkData.map((fw, i) => (
                    <div key={fw.name} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5" style={{ backgroundColor: FRAMEWORK_COLORS[i % FRAMEWORK_COLORS.length] }} />
                      <span className="text-[10px] font-medium text-zinc-600">{fw.name} ({fw.value})</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center border border-dashed border-zinc-200">
                <p className="text-xs text-zinc-400 uppercase tracking-wide">No framework data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* ── QUICK ACTIONS + RECENT CONTRACTS ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Quick Actions - 1 col */}
          <div
            className={`border-2 border-zinc-200 bg-white transition-all duration-500 ${
              animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "700ms" }}
            data-testid="quick-actions"
          >
            <div className="px-5 py-4 border-b-2 border-zinc-200 bg-zinc-950 text-white">
              <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide flex items-center gap-2">
                <Zap size={14} className="text-[#FF4F00]" />
                Quick Actions
              </h2>
            </div>
            <div className="p-3 space-y-1">
              {[
                { id: "generator", icon: Plus, label: "Generate Contract", desc: "AI-powered drafting", color: "#0047AB" },
                { id: "analyzer", icon: Eye, label: "Analyze Contract", desc: "Upload & scan risks", color: "#FF4F00" },
                { id: "compare", icon: Layers, label: "Compare Contracts", desc: "Side-by-side delta", color: "#8B5CF6" },
                { id: "risk-analysis", icon: Shield, label: "Risk Analysis", desc: "Portfolio risk audit", color: "#10B981" },
                { id: "delay-analysis", icon: Clock, label: "Delay Analysis", desc: "Forensic schedule review", color: "#F59E0B" },
                { id: "controls", icon: Target, label: "Project Controls", desc: "KPIs & performance", color: "#DC2626" },
              ].map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => setActiveView(action.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-50 transition-all group border border-transparent hover:border-zinc-200"
                    data-testid={`action-${action.id}`}
                  >
                    <div className="p-2 border border-zinc-200 group-hover:border-black transition-colors" style={{ color: action.color }}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wide">{action.label}</p>
                      <p className="text-[10px] text-zinc-400">{action.desc}</p>
                    </div>
                    <ArrowUpRight size={12} className="text-zinc-300 group-hover:text-black transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recent Contracts - 2 cols */}
          <div
            className={`lg:col-span-2 border-2 border-zinc-200 bg-white transition-all duration-500 ${
              animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "800ms" }}
            data-testid="recent-contracts"
          >
            <div className="px-5 py-4 border-b-2 border-zinc-200 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide">Recent Contracts</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">{contracts.length} total in portfolio</p>
              </div>
              {contracts.length > 0 && (
                <Button
                  onClick={() => setActiveView("contracts")}
                  variant="outline"
                  className="text-[10px] px-3 py-1.5 h-auto uppercase tracking-wider border-2 border-zinc-200 hover:border-black"
                  data-testid="view-all-contracts"
                >
                  View All <ChevronRight size={10} className="ml-1" />
                </Button>
              )}
            </div>
            {contracts.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {contracts.slice(0, 6).map((contract, i) => {
                  const riskColor = (contract.risk_score || 0) > 70 ? "#FF4F00"
                    : (contract.risk_score || 0) > 50 ? "#F59E0B" : "#10B981";
                  const riskLabel = (contract.risk_score || 0) > 70 ? "HIGH"
                    : (contract.risk_score || 0) > 50 ? "MED" : "LOW";
                  return (
                    <div
                      key={contract.id || i}
                      className="px-5 py-3.5 flex items-center gap-4 hover:bg-zinc-50 transition-colors group cursor-pointer"
                      onClick={() => {
                        if (contract.id) {
                          setActiveView("contracts");
                        }
                      }}
                      data-testid={`contract-row-${i}`}
                    >
                      {/* Risk indicator bar */}
                      <div className="w-1 h-10 flex-shrink-0" style={{ backgroundColor: riskColor }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate group-hover:text-[#0047AB] transition-colors">
                          {contract.project_name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] font-medium text-zinc-500 uppercase">{contract.framework}</span>
                          <span className="text-zinc-300">|</span>
                          <span className="text-[10px] text-zinc-400">{contract.sector}</span>
                          {contract.contract_value && (
                            <>
                              <span className="text-zinc-300">|</span>
                              <span className="text-[10px] text-zinc-400">{fmtVal(contract.contract_value)}</span>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Risk badge */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: riskColor }}>
                              {riskLabel}
                            </span>
                            <div
                              className="w-8 h-8 flex items-center justify-center border-2 text-xs font-black font-['Chivo']"
                              style={{ borderColor: riskColor, color: riskColor }}
                            >
                              {contract.risk_score || 0}
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-zinc-900 text-white border-zinc-700 text-xs">
                          Risk Score: {contract.risk_score || 0}/100
                        </TooltipContent>
                      </Tooltip>
                      {/* Actions */}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <a
                          href={`${API}/contracts/download/${contract.filename}`}
                          className="p-1.5 hover:bg-zinc-200 transition-colors"
                          title="Download DOCX"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download size={12} />
                        </a>
                        <a
                          href={`${API}/contracts/${contract.id}/pdf`}
                          className="p-1.5 hover:bg-zinc-200 transition-colors"
                          title="Download PDF"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FileDown size={12} />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-12 text-center">
                <FileText className="mx-auto mb-3 text-zinc-300" size={32} />
                <p className="text-sm font-bold text-zinc-500 mb-1">No Contracts Yet</p>
                <p className="text-xs text-zinc-400 mb-4">Generate your first contract to populate the dashboard</p>
                <Button
                  onClick={() => setActiveView("generator")}
                  className="btn-brutal bg-black text-white hover:bg-zinc-800 text-xs px-5 py-2 h-auto"
                  data-testid="empty-cta-generate"
                >
                  <Plus size={12} className="mr-2" /> Generate First Contract
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* ── PLATFORM CAPABILITIES BAR ── */}
        <div
          className={`border-2 border-zinc-200 bg-white p-5 transition-all duration-500 ${
            animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
          style={{ transitionDelay: "900ms" }}
          data-testid="capabilities-bar"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            {[
              { label: "AI Drafting", icon: Zap, active: true },
              { label: "Risk Engine", icon: Shield, active: true },
              { label: "Delay Analysis", icon: Clock, active: true },
              { label: "15 Frameworks", icon: Library, active: true },
              { label: "PDF Export", icon: FileDown, active: true },
              { label: "Real-time Collab", icon: Activity, active: true },
            ].map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.label} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#10B981]" />
                  <Icon size={12} className="text-zinc-400" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">{cap.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default DashboardView;
